import React, { useEffect, useState } from "react";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Building2, MapPin, CheckCircle2, XCircle, Clock, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PLAN_LABEL, label } from "@/lib/labels";

export default function SuperHospitals() {
  const { t } = useApp();
  const [items, setItems] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", city: "", contact_email: "", contact_phone: "",
    admin_name: "", admin_password: "",
    commune: "", quartier: "", avenue: "", ville: "", province: "", pays: "RDC",
  });

  const load = async () => {
    const [r, rp] = await Promise.all([
      axios.get(`${API}/hospitals`),
      axios.get(`${API}/ministry/provinces`).catch(() => ({ data: [] })),
    ]);
    setItems(r.data);
    setProvinces(rp.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/hospitals`, { ...form, status: "active" });
      toast.success("Hôpital créé et activé");
      setOpen(false);
      setForm({
        name: "", city: "", contact_email: "", contact_phone: "",
        admin_name: "", admin_password: "",
        commune: "", quartier: "", avenue: "", ville: "", province: "", pays: "RDC",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/hospitals/${id}/status?status=${status}`);
      toast.success(`Statut: ${status}`);
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const statusBadge = (s) => {
    const map = {
      active: { c: "#2E7D32", l: "Actif", i: CheckCircle2 },
      pending: { c: "#E3A72F", l: "En attente", i: Clock },
      inactive: { c: "#C62828", l: "Inactif", i: XCircle },
    };
    const m = map[s] || map.pending;
    const Icon = m.i;
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
            style={{ background: `${m.c}15`, color: m.c }}>
        <Icon className="w-3 h-3"/> {m.l}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="super-hospitals">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-[Outfit] font-bold">{t.super.hospitals}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1E4631] hover:bg-[#163424] text-white" data-testid="add-hospital-btn">
              <Plus className="w-4 h-4 mr-2"/> {t.super.new_hospital}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{t.super.new_hospital}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="hospital-form">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t.reg.h_name}</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="h-name"/></div>
                <div><Label>{t.reg.h_city}</Label><Input required value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} data-testid="h-city"/></div>
                <div><Label>{t.reg.h_email}</Label><Input type="email" required value={form.contact_email} onChange={(e)=>setForm({...form, contact_email: e.target.value})} data-testid="h-email"/></div>
                <div><Label>{t.reg.h_phone}</Label><Input required value={form.contact_phone} onChange={(e)=>setForm({...form, contact_phone: e.target.value})} data-testid="h-phone"/></div>
                <div><Label>{t.reg.a_name}</Label><Input required value={form.admin_name} onChange={(e)=>setForm({...form, admin_name: e.target.value})} data-testid="h-admin"/></div>
                <div><Label>{t.reg.a_pw}</Label><Input type="password" required minLength={8} value={form.admin_password} onChange={(e)=>setForm({...form, admin_password: e.target.value})} data-testid="h-pw"/></div>
                <div className="col-span-2 border-t border-[#E5EBE7] pt-3 mt-1">
                  <div className="text-xs font-semibold uppercase text-[#5C6B61] mb-1">Adresse physique structurée</div>
                </div>
                <div><Label>Commune</Label><Input value={form.commune} onChange={(e)=>setForm({...form, commune: e.target.value})} placeholder="Ex: Gombe, Kalamu…" data-testid="h-commune"/></div>
                <div><Label>Quartier</Label><Input value={form.quartier} onChange={(e)=>setForm({...form, quartier: e.target.value})} placeholder="Ex: Q.7 Lemba" data-testid="h-quartier"/></div>
                <div className="col-span-2"><Label>Avenue et N°</Label><Input value={form.avenue} onChange={(e)=>setForm({...form, avenue: e.target.value})} placeholder="Ex: Av. Kasa-Vubu N°123" data-testid="h-avenue"/></div>
                <div><Label>Ville</Label><Input value={form.ville} onChange={(e)=>setForm({...form, ville: e.target.value})} placeholder="Ex: Kinshasa" data-testid="h-ville"/></div>
                <div>
                  <Label>Province</Label>
                  <Select value={form.province || "__none__"} onValueChange={(v)=>setForm({...form, province: v === "__none__" ? "" : v})}>
                    <SelectTrigger data-testid="h-province"><SelectValue placeholder="Choisir…"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Non précisé —</SelectItem>
                      {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Pays</Label><Input value={form.pays} onChange={(e)=>setForm({...form, pays: e.target.value})} data-testid="h-pays"/></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={()=>setOpen(false)}>{t.dash.cancel}</Button>
                <Button type="submit" className="bg-[#1E4631]" data-testid="h-save">{t.dash.save}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {items.length === 0 ? (
          <div className="bg-white p-12 text-center text-[#5C6B61] rounded-xl border border-[#E5EBE7]" data-testid="empty-hospitals">Aucun hôpital</div>
        ) : items.map((h) => (
          <div key={h.id} className="bg-white rounded-xl border border-[#E5EBE7] p-6 hover:shadow-md transition" data-testid={`hospital-${h.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#1E4631]/10 grid place-items-center">
                  <Building2 className="w-6 h-6 text-[#1E4631]"/>
                </div>
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-[Outfit] font-bold text-lg">{h.name}</h3>
                    {statusBadge(h.status)}
                    <span className="text-xs uppercase px-2 py-0.5 rounded-full bg-[#FDFBF7] text-[#5C6B61]">{label(PLAN_LABEL, h.plan)}</span>
                  </div>
                  <div className="mt-1 text-sm text-[#5C6B61] flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{h.city}</span>
                    <span>·</span>
                    <span>{h.contact_email}</span>
                    <span>·</span>
                    <span>{h.contact_phone}</span>
                  </div>
                  {(h.commune || h.quartier || h.avenue || h.province) && (
                    <div className="mt-1 text-xs text-[#5C6B61] italic">
                      {[h.avenue, h.quartier, h.commune, h.ville, h.province, h.pays].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <div className="mt-3 flex gap-4 text-xs text-[#5C6B61]">
                    <span><strong className="text-[#1A1D1A]">{h.staff_count}</strong> équipe</span>
                    <span><strong className="text-[#1A1D1A]">{h.patient_count}</strong> patients</span>
                    <span><strong className="text-[#1A1D1A]">{h.sms_count}</strong> SMS</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 items-center">
                <Link to={`/super-admin/hospitals/${h.id}`} data-testid={`view-${h.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#1E4631] text-[#1E4631] hover:bg-[#1E4631] hover:text-white text-xs font-medium transition">
                  Détails <ArrowUpRight className="w-3 h-3"/>
                </Link>
                {h.status === "pending" && (
                  <Button onClick={() => setStatus(h.id, "active")} className="bg-[#2E7D32] hover:bg-[#256628] text-white text-sm" data-testid={`approve-${h.id}`}>
                    <CheckCircle2 className="w-4 h-4 mr-1.5"/> {t.super.approve}
                  </Button>
                )}
                {h.status === "active" && (
                  <Button onClick={() => setStatus(h.id, "inactive")} variant="outline" className="text-sm border-[#C62828] text-[#C62828] hover:bg-red-50" data-testid={`deactivate-${h.id}`}>
                    {t.super.deactivate}
                  </Button>
                )}
                {h.status === "inactive" && (
                  <Button onClick={() => setStatus(h.id, "active")} className="bg-[#1E4631] hover:bg-[#163424] text-white text-sm" data-testid={`activate-${h.id}`}>
                    {t.super.activate}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
